import { useEffect, useMemo, useRef, useState } from "react";
import { Card, FormField, FormSubmit, Button, Dropdown } from "@common";
import { showError, showSuccess } from "@common";
import {
  useCreateCollectionBreakdown,
  useInsuranceEftDay,
  useUpdateCollectionBreakdown,
} from "@api/hooks";
import { collectionBreakdownSchema } from "@utils/schema";
import { getApiErrorMessage } from "@utils/apiErrorUtils";
import { roundTo2 } from "@utils/utils";
import { formatMoney } from "@utils/format";
import { useFormInteractionTime } from "@hooks/useFormInteractionTime";
import { FORM_IDS } from "@utils";
import { REFUND_TYPE_OPTIONS } from "@utils/depositSheetColumns";

// Extended breakdown: nine explicit line items; "Other" is derived so the sum matches clinic total_collection.

const toInput = (v) => (v === null || v === undefined ? "" : String(Number(v) || v));

const EMPTY_VALS = {
  cash: "",
  debit: "",
  mastercard: "",
  visa: "",
  amex: "",
  checks: "",
  insurance_check: "",
  personal_eft: "",
  insurance_eft: "",
  insurance_checks_quantity: "",
  insurance_efts_quantity: "",
  // One row per method a refund went back through; `refund` is saved as their sum.
  refunds: [{ type: "", amount: "" }],
  collection_note: "",
};

// Optional whole-number count; blank saves null.
const toCount = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

// User-entered amounts only (excludes auto "Other").
const LINE_KEYS = [
  "cash",
  "debit",
  "mastercard",
  "visa",
  "amex",
  "checks",
  "insurance_check",
  "personal_eft",
  "insurance_eft",
];

function nz(v) {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
}

function sumLineAmounts(vals) {
  return LINE_KEYS.reduce((s, k) => s + nz(vals[k]), 0);
}

function sumRefunds(refunds) {
  return refunds.reduce((s, r) => s + nz(r.amount), 0);
}

// Saved refunds → form rows. A day saved before refunds were split by method
// has only the `refund` total: it becomes one row with no type.
function refundRows(existing) {
  if (existing.refunds?.length) {
    return existing.refunds.map((r) => ({ type: r.type ?? "", amount: toInput(r.amount) }));
  }
  if (nz(existing.refund) !== 0) return [{ type: "", amount: toInput(existing.refund) }];
  return [{ type: "", amount: "" }];
}

// Hydrate form from API: prefer granular fields when present; else fall back to legacy collection_credit / collection_eft.
function valuesFromExisting(existing) {
  if (!existing) return { ...EMPTY_VALS };

  const cardGranular =
    nz(existing.collection_debit) +
    nz(existing.collection_mastercard) +
    nz(existing.collection_visa) +
    nz(existing.collection_amex);
  const hasCardGranular = cardGranular > 0;

  const hasEftGranular =
    (existing.collection_personal_eft != null && nz(existing.collection_personal_eft) !== 0) ||
    (existing.collection_insurance_eft != null && nz(existing.collection_insurance_eft) !== 0);

  return {
    cash: toInput(existing.collection_cash),
    debit: toInput(
      hasCardGranular
        ? existing.collection_debit
        : (existing.collection_credit ?? existing.collection_debit),
    ),
    mastercard: toInput(existing.collection_mastercard),
    visa: toInput(existing.collection_visa),
    amex: toInput(existing.collection_amex),
    checks: toInput(existing.collection_checks),
    insurance_check: toInput(existing.collection_insurance_check),
    personal_eft: toInput(
      hasEftGranular
        ? existing.collection_personal_eft
        : (existing.collection_eft ?? existing.collection_personal_eft),
    ),
    insurance_eft: toInput(existing.collection_insurance_eft),
    insurance_checks_quantity: toInput(existing.insurance_checks_quantity),
    insurance_efts_quantity: toInput(existing.insurance_efts_quantity),
    refunds: refundRows(existing),
    collection_note: existing.collection_note ?? "",
  };
}

/**
 * Small hint under a field: the amount read from the day's uploaded files. Only a
 * suggestion — it never fills the field, and a different amount warns but does
 * not block submit.
 */
function SuggestedAmount({ suggested, value, checkFiles }) {
  if (suggested === null) return null;
  const differs = Math.round(nz(value) * 100) !== Math.round(suggested * 100);
  return (
    <>
      <p className="mt-1 text-xs text-palette-slate-secondary">
        Suggested: {formatMoney(suggested)}
        {checkFiles ? " · check files" : ""}
      </p>
      {differs && (
        <p className="mt-1 text-xs text-palette-rose-badge">Does not match suggested amount.</p>
      )}
    </>
  );
}

export default function CollectionBreakdown({
  clinicId,
  date,
  collectionBreakdowns,
  clinicData,
  canEdit = true,
}) {
  const createCollectionBreakdown = useCreateCollectionBreakdown();
  const updateCollectionBreakdown = useUpdateCollectionBreakdown();

  const { formTrackingProps, recordSubmitSuccess } = useFormInteractionTime(
    FORM_IDS.COLLECTION_BREAKDOWN_REPORT,
  );

  const existing = useMemo(() => {
    const items = collectionBreakdowns?.results || collectionBreakdowns || [];
    return items.length > 0 ? items[0] : null;
  }, [collectionBreakdowns]);

  // Authoritative total from clinic report summary (read-only).
  const fixedTotal = Number(clinicData?.summary?.total_collection ?? 0);

  const [isEditing, setIsEditing] = useState(false);
  const [touched, setTouched] = useState(false);
  const [vals, setVals] = useState(EMPTY_VALS);

  useEffect(() => {
    if (existing) {
      setVals(valuesFromExisting(existing));
      setIsEditing(false);
    } else {
      setVals({ ...EMPTY_VALS });
      setIsEditing(true);
    }
    setTouched(false);
  }, [existing]);

  // Gap between entered lines (plus refund) and fixed total becomes collection_other on save.
  // Refund may be negative and must participate in the reconciliation so Other adjusts accordingly.
  const refundAmount = sumRefunds(vals.refunds);
  const calculatedOther = Math.max(fixedTotal - sumLineAmounts(vals) - refundAmount, 0);
  const calculatedTotal = sumLineAmounts(vals) + refundAmount + calculatedOther;

  const hasMismatch = useMemo(() => {
    return Math.abs(fixedTotal - calculatedTotal) > 0.01;
  }, [fixedTotal, calculatedTotal]);

  // Toast once per mismatch spell while editing (avoids spam on every keystroke).
  const mismatchToastShown = useRef(false);
  useEffect(() => {
    if (!isEditing) {
      mismatchToastShown.current = false;
      return;
    }
    if (!hasMismatch) {
      mismatchToastShown.current = false;
      return;
    }
    if (!mismatchToastShown.current) {
      showError(
        `Mismatch! Total must equal ${formatMoney(
          fixedTotal,
        )}, but calculated is ${formatMoney(calculatedTotal)}`,
      );
      mismatchToastShown.current = true;
    }
  }, [hasMismatch, isEditing, fixedTotal, calculatedTotal]);

  const errors = useMemo(() => {
    const result = collectionBreakdownSchema.safeParse(vals);
    if (result.success) return {};

    const err = {};
    result.error?.errors?.forEach((e) => {
      err[e.path[0]] = e.message;
    });
    return err;
  }, [vals]);

  const canSubmit = Object.keys(errors).length === 0 && isEditing && !hasMismatch;

  // Insurance EFTs and insurance cheques read from the day's uploaded files.
  const { data: eftData } = useInsuranceEftDay({ clinic: clinicId, date });
  const suggestedInsuranceEft =
    eftData?.suggested_total != null ? Number(eftData.suggested_total) : null;
  const suggestedInsuranceCheque =
    eftData?.suggested_cheque_total != null ? Number(eftData.suggested_cheque_total) : null;

  // Persist granular fields; keep collection_credit / collection_eft as rollups for older APIs and aggregates.
  function buildAmountPayload() {
    const cash = nz(vals.cash);
    const debit = nz(vals.debit);
    const mastercard = nz(vals.mastercard);
    const visa = nz(vals.visa);
    const amex = nz(vals.amex);
    const checks = nz(vals.checks);
    const insurance_check = nz(vals.insurance_check);
    const personal_eft = nz(vals.personal_eft);
    const insurance_eft = nz(vals.insurance_eft);
    const refundRowsEntered = vals.refunds.filter((r) => r.amount !== "");
    const refund = sumRefunds(refundRowsEntered);
    const cardRollup = debit + mastercard + visa + amex;
    const eftRollup = personal_eft + insurance_eft;
    const other = Math.max(
      fixedTotal - (cash + cardRollup + checks + insurance_check + eftRollup + refund),
      0,
    );

    return {
      collection_total: roundTo2(fixedTotal),
      collection_cash: roundTo2(cash),
      collection_debit: roundTo2(debit),
      collection_mastercard: roundTo2(mastercard),
      collection_visa: roundTo2(visa),
      collection_amex: roundTo2(amex),
      collection_checks: roundTo2(checks),
      collection_insurance_check: roundTo2(insurance_check),
      collection_personal_eft: roundTo2(personal_eft),
      collection_insurance_eft: roundTo2(insurance_eft),
      insurance_checks_quantity: toCount(vals.insurance_checks_quantity),
      insurance_efts_quantity: toCount(vals.insurance_efts_quantity),
      collection_credit: roundTo2(cardRollup),
      collection_eft: roundTo2(eftRollup),
      collection_other: roundTo2(other),
      refund: refundRowsEntered.length ? roundTo2(refund) : null,
      refunds: refundRowsEntered.map((r) => ({
        type: r.type || null,
        amount: roundTo2(Number(r.amount)),
      })),
      collection_note: vals.collection_note?.trim() ? vals.collection_note : null,
    };
  }

  function onSubmit(e) {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;

    const ok = (msg) => {
      recordSubmitSuccess();
      showSuccess(msg);
      setIsEditing(false);
      setTouched(false);
    };
    const bad = (error) => {
      showError(getApiErrorMessage(error));
      console.error(error);
    };

    const payload = buildAmountPayload();

    if (existing?.id) {
      updateCollectionBreakdown.mutate(
        { clinic: clinicId, id: existing.id, data: payload },
        {
          onSuccess: () => ok("Collection breakdown updated!"),
          onError: bad,
        },
      );
    } else {
      createCollectionBreakdown.mutate(
        { clinic: clinicId, data: { date, ...payload } },
        {
          onSuccess: () => ok("Collection breakdown saved!"),
          onError: bad,
        },
      );
    }
  }

  const field = (key, label) => (
    <FormField
      label={label}
      type="number"
      min="0"
      step="any"
      value={vals[key]}
      onChange={(e) => setVals((v) => ({ ...v, [key]: e.target.value }))}
      disabled={!isEditing}
      error={errors[key]}
      showError={touched}
    />
  );
  const setRefund = (i, patch) =>
    setVals((v) => ({
      ...v,
      refunds: v.refunds.map((r, j) => (j === i ? { ...r, ...patch } : r)),
    }));
  // Optional count beside an amount, e.g. how many insurance EFTs made it up.
  const quantity = (key) => (
    <FormField
      label="Qty"
      type="number"
      min="0"
      step="1"
      value={vals[key]}
      onChange={(e) => setVals((v) => ({ ...v, [key]: e.target.value }))}
      disabled={!isEditing}
      placeholder="Optional"
      error={errors[key]}
      showError={touched}
    />
  );

  return (
    <Card className="mt-8">
      <h3 className="text-lg font-semibold mb-4">
        <span className="font-bold">{clinicId}</span>
        <span>{"'s Collection Breakdown Report"}</span>
      </h3>

      <FormSubmit onSubmit={onSubmit} {...formTrackingProps}>
        <FormField
          label="Total (ReadOnly)"
          value={formatMoney(fixedTotal)}
          disabled
          className="mb-4"
        />

        <p className="text-sm font-medium text-palette-slate-badge mb-2">Collection by method</p>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {field("cash", "Cash")}
          {field("debit", "Debit")}
          {field("mastercard", "Mastercard")}
          {field("visa", "Visa")}
          {field("amex", "Amex")}
          {field("checks", "Checks")}
          <div>
            <div className="grid grid-cols-[1fr_6rem] gap-2">
              {field("insurance_check", "Insurance check")}
              {quantity("insurance_checks_quantity")}
            </div>
            <SuggestedAmount
              suggested={suggestedInsuranceCheque}
              value={vals.insurance_check}
              checkFiles={eftData?.cheque_needs_review_count > 0}
            />
          </div>
          {field("personal_eft", "Personal EFTs")}
          <div>
            <div className="grid grid-cols-[1fr_6rem] gap-2">
              {field("insurance_eft", "Insurance EFTs")}
              {quantity("insurance_efts_quantity")}
            </div>
            <SuggestedAmount
              suggested={suggestedInsuranceEft}
              value={vals.insurance_eft}
              checkFiles={eftData?.needs_review_count > 0}
            />
          </div>
          <div className="flex flex-col gap-2 md:col-span-2">
            {vals.refunds.map((r, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_2rem] items-end gap-2">
                <FormField
                  label={i === 0 ? "Refund (+/-)" : undefined}
                  type="number"
                  step="any"
                  value={r.amount}
                  onChange={(e) => setRefund(i, { amount: e.target.value })}
                  disabled={!isEditing}
                />
                <Dropdown
                  label={i === 0 ? "Refund type" : undefined}
                  value={r.type}
                  onChange={(type) => setRefund(i, { type })}
                  options={REFUND_TYPE_OPTIONS}
                  emptyOption
                  disabled={!isEditing}
                />
                {isEditing && vals.refunds.length > 1 ? (
                  <button
                    type="button"
                    onClick={() =>
                      setVals((v) => ({ ...v, refunds: v.refunds.filter((_, j) => j !== i) }))
                    }
                    title="Remove this refund"
                    className="mb-1 h-8 rounded text-palette-slate-secondary hover:bg-palette-rose-soft hover:text-palette-rose-heading"
                  >
                    ×
                  </button>
                ) : (
                  <span />
                )}
              </div>
            ))}
            {touched && errors.refunds && (
              <p className="text-xs text-palette-rose-hover">{errors.refunds}</p>
            )}
            {isEditing && (
              <button
                type="button"
                onClick={() =>
                  setVals((v) => ({ ...v, refunds: [...v.refunds, { type: "", amount: "" }] }))
                }
                className="self-start text-sm font-medium text-palette-indigo-badge hover:underline"
              >
                + Add refund
              </button>
            )}
          </div>
          <FormField
            label="Other (auto-calculated)"
            value={formatMoney(calculatedOther)}
            disabled
          />
          <FormField
            label="Note"
            type="text"
            value={vals.collection_note}
            onChange={(e) => setVals((v) => ({ ...v, collection_note: e.target.value }))}
            disabled={!isEditing}
            placeholder="Optional note about this day's collection"
            error={errors.collection_note}
            showError={touched}
          />
        </div>

        <div className="mt-6 flex justify-end gap-2">
          {!isEditing && existing && canEdit && (
            <Button type="button" onClick={() => setIsEditing(true)} variant="success">
              Edit
            </Button>
          )}

          {isEditing && canEdit && (
            <>
              {existing && (
                <Button
                  type="button"
                  onClick={() => {
                    setVals(valuesFromExisting(existing));
                    setIsEditing(false);
                    setTouched(false);
                  }}
                  variant="secondary"
                >
                  Cancel
                </Button>
              )}
              <Button type="submit" variant="success" disabled={!canSubmit}>
                {existing ? "Update" : "Submit"}
              </Button>
            </>
          )}
        </div>

        <p className="mt-2 text-xs text-palette-gray-secondary">
          Enter amounts for each method; Other fills the gap to match the clinic total above. All
          fields are required.
        </p>
      </FormSubmit>
    </Card>
  );
}
